// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { CYCLICAL_REFERENCE_ERROR_CODE } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { DetailPageBody } from '@/components/detail-page-body';
import { DetailPageHeader } from '@/components/detail-page-header';
import { DetailPageHeaderField } from '@/components/detail-page-header-field';
import { EditablePropertyButton } from '@/components/editable-property-button';
import { HorizontalNavigationLink } from '@/components/horizontal-navigation-link';
import { IntegrationStatus } from '@/components/integration-status';
import { Link } from '@/components/link';
import { LocationName } from '@/components/location-name';
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import {
  AddIcCallOutlined,
  InfoOutlined,
  NoteAddOutlined,
  Sync,
} from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { useDisclosure } from '@/hooks/use-disclosure';
import { NoteDialog } from '@/note/components/note-dialog';
import { addProjectNote } from '@/project/api/add-project-note';
import { getProject } from '@/project/api/get-project';
import { ProjectCurrencySource } from '@/project/api/types';
import { ProjectActionButton } from '@/project/components/project-action-button';
import { ProjectAttachmentsSection } from '@/project/components/project-attachments-section';
import { ProjectCustomerDialog } from '@/project/components/project-customer-dialog';
import { ProjectCustomerLabel } from '@/project/components/project-customer-label';
import { ProjectDateRangeControl } from '@/project/components/project-date-range-control';
import { ProjectDefaultLocationDialog } from '@/project/components/project-default-location-dialog';
import { ProjectDescriptionControl } from '@/project/components/project-description-control';
import { ProjectDetailCard } from '@/project/components/project-detail-card';
import { ProjectDetailField } from '@/project/components/project-detail-field';
import { ProjectDetailsSkeleton } from '@/project/components/project-details-skeleton';
import { ProjectKeyDatesSection } from '@/project/components/project-key-dates-section';
import { ProjectLocationsLabel } from '@/project/components/project-locations-label';
import { ProjectNameDialog } from '@/project/components/project-name-dialog';
import { ProjectPreviousProjectControl } from '@/project/components/project-previous-project-control';
import { ProjectTotalContractValueControl } from '@/project/components/project-total-contract-value-control';
import { ProjectWorkflowDisplay } from '@/project/components/project-workflow-display';
import { ProjectProvider } from '@/project/contexts/project-context';
import { PROJECT_INVOICED_CHARGE_LOCKED_TOOLTIP } from '@/project/utilities/project-currency';
import { CreateTicketDialog } from '@/ticket/components/create-ticket-dialog';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Outlet, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const PROJECT_CURRENCY_SOURCE_TOOLTIP: Record<ProjectCurrencySource, string> = {
  INVOICED_CHARGE: PROJECT_INVOICED_CHARGE_LOCKED_TOOLTIP,
  PROJECT_VALUE: 'The project currency is set by the project value',
  LINE_ITEMS: 'The project currency is set by the line items',
  DISCOUNT: 'The project currency is set by the discount',
};

const PROJECT_CURRENCY_UNSET_TOOLTIP =
  'No currency has been set for this project yet';

export const Route = createFileRoute('/_app/projects/$projectKey')({
  component: ProjectRoute,
});

function ProjectRoute() {
  const { projectKey } = Route.useParams();

  return (
    <NotFoundErrorBoundary
      entityType="project"
      listPath="/projects"
      listLabel="Go to projects"
    >
      <Suspense fallback={<ProjectDetailsSkeleton />}>
        <ProjectContent projectKey={projectKey} />
      </Suspense>
    </NotFoundErrorBoundary>
  );
}

function ProjectContent(props: { projectKey: string }) {
  const nameDialog = useDisclosure();
  const customerDialog = useDisclosure();
  const defaultLocationDialog = useDisclosure();
  const noteDialog = useDisclosure();
  const ticketDialog = useDisclosure();
  const [updateError, setUpdateError] = useState<AxiosError | null>(null);
  const navigate = useNavigate();

  const queryKey = useMemo(
    () => ['project', props.projectKey],
    [props.projectKey]
  );
  const { data } = useSuspenseQuery({
    queryKey: queryKey,
    queryFn: () => getProject(props.projectKey),
    // Project data backs the entire layout (line items, audits, tickets,
    // costs, etc.). The layout route stays mounted across child-tab
    // navigation, so a default staleTime would serve cached data for the
    // full session and miss out-of-band changes (another user's edit,
    // a DB reset, etc.). Treat the data as always-stale so every
    // invalidate (e.g. drawer-open, mutation success) triggers a
    // refetch, and any remount refreshes from the server.
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Mutation for adding notes
  const addNoteMutation = useMutation({
    mutationFn: (content: string) => addProjectNote(props.projectKey, content),
    onSuccess: () => {
      noteDialog.close();
      queryClient.invalidateQueries({ queryKey });
      showToast({
        duration: 3000,
        element: <Box>Note added successfully</Box>,
      });
      navigate({
        to: '/projects/$projectKey/notes',
        params: { projectKey: props.projectKey },
      });
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      showToast({
        duration: 5000,
        element: <Box>Failed to add note</Box>,
      });
    },
  });

  // Handler for successful ticket creation
  const handleTicketCreated = useCallback(() => {
    showToast({
      duration: 3000,
      element: <Box>Ticket created successfully</Box>,
    });
    navigate({
      to: '/projects/$projectKey/tickets',
      params: { projectKey: props.projectKey },
    });
  }, [showToast, navigate, props.projectKey]);

  const handleSyncComplete = useCallback(() => {
    showToast({
      duration: 5000,
      element: (
        <Box>
          Resync complete,{' '}
          <Link
            reloadDocument
            to="/projects/$projectKey"
            params={{ projectKey: props.projectKey }}
          >
            click to refresh
          </Link>
        </Box>
      ),
    });
  }, [showToast, props.projectKey]);

  useEffect(() => {
    return () => {
      // Clear the data cache when the component unmounts
      // Failure to do this can result in a stale data set triggering a toast letting them know it was resynced
      queryClient.removeQueries({ queryKey: queryKey });
    };
  }, [queryClient, queryKey]);

  // Create a reference that can track the sync state
  // This is used to prevent the toast from showing up at times that are inappropriate such as an unmount or page refresh.
  // If the resync should be shown after a mount -> remount, the logic re-triggers with the toast
  const syncState = useRef<boolean | undefined>(undefined);
  const isSyncing = data.project.integrations[0]?.isSyncing ?? false;
  useEffect(() => {
    if (isSyncing) {
      const interval = setInterval(() => {
        // Trigger a refetch of the project data
        queryClient.invalidateQueries({ queryKey: queryKey });
      }, 5000);
      if (!syncState.current) {
        syncState.current = true;
      }
      return () => {
        syncState.current = false;
        clearInterval(interval);
      };
    }

    if (syncState.current === false) {
      handleSyncComplete();
      syncState.current = undefined;
    }
  }, [isSyncing, queryClient, queryKey, handleSyncComplete]);

  // Check for cyclical reference error
  const hasCyclicalError = useMemo(() => {
    if (!updateError) {
      return false;
    }
    const apiError = handleAPIError(updateError);
    return apiError.apiStatusCode === CYCLICAL_REFERENCE_ERROR_CODE;
  }, [updateError]);
  return (
    <Container maxWidth="xl">
      <DetailPageHeader
        parentBreadcrumb={
          <Link
            to="/projects"
            variant="body2"
            color="text.secondary"
            sx={{ display: 'block' }}
          >
            Projects
          </Link>
        }
        currentBreadcrumb={data.project.name}
        disableTitleTypography
        title={
          <>
            {data.project.integrations.length > 0 && (
              <ConditionalTooltip
                sx={{
                  position: 'relative',
                  display: 'flex',
                }}
                tooltipText={
                  isSyncing
                    ? `This project is re-syncing from ${data.project.integrations[0].type}`
                    : undefined
                }
              >
                <IntegrationStatus
                  status={data.project.integrations}
                  width={'24px'}
                  iconOpacity={isSyncing ? 0.8 : 1}
                  entityType="project"
                  entityKey={props.projectKey}
                  entityDisplayName={data.project.name}
                  onResolved={() => {
                    void queryClient.invalidateQueries({ queryKey: queryKey });
                  }}
                >
                  {isSyncing && (
                    <Sync
                      color={'action'}
                      sx={{
                        transform: 'scale(0.75)',
                        transformOrigin: 'top right',
                        position: 'absolute',
                        right: -8,
                        top: -3,
                        zIndex: 5,
                        opacity: 1,
                      }}
                    />
                  )}
                </IntegrationStatus>
              </ConditionalTooltip>
            )}
            <EditablePropertyButton variant="h5" onClick={nameDialog.open}>
              {data.project.name}
            </EditablePropertyButton>
          </>
        }
        titleAdornment={
          data.project.isArchived && <Chip size="small" label="Archived" />
        }
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<NoteAddOutlined />}
              onClick={noteDialog.open}
            >
              Add note
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcCallOutlined />}
              onClick={ticketDialog.open}
            >
              Open ticket
            </Button>
            <ProjectActionButton
              project={data.project}
              queryKey={queryKey}
              isCRMEnabled={
                data.project.integrations.length > 0 &&
                data.project.integrations[0].link !== null &&
                data.project.integrations[0].link !== ''
              }
            />
          </Stack>
        }
      >
        <DetailPageHeaderField label="Previous project" disableValueTypography>
          <Box>
            <ProjectPreviousProjectControl
              project={data.project}
              onError={setUpdateError}
            />
          </Box>
        </DetailPageHeaderField>
      </DetailPageHeader>
      {hasCyclicalError && (
        <Alert
          severity="error"
          onClose={() => setUpdateError(null)}
          sx={{ marginX: 3, marginTop: 2 }}
        >
          Cannot set previous project: cyclical project reference is not allowed
        </Alert>
      )}
      <ProjectNameDialog
        project={data.project}
        isOpen={nameDialog.isOpen}
        onClose={nameDialog.close}
      />
      <ProjectCustomerDialog
        project={data.project}
        isOpen={customerDialog.isOpen}
        onClose={customerDialog.close}
      />
      <ProjectDefaultLocationDialog
        project={data.project}
        isOpen={defaultLocationDialog.isOpen}
        onClose={defaultLocationDialog.close}
      />
      <NoteDialog
        isOpen={noteDialog.isOpen}
        onClose={noteDialog.close}
        onSubmit={addNoteMutation.mutate}
        isSubmitting={addNoteMutation.isPending}
        title="Add project note"
      />
      <CreateTicketDialog
        isOpen={ticketDialog.isOpen}
        onClose={ticketDialog.close}
        onSuccess={handleTicketCreated}
        forProjectIds={[data.project]}
        additionalQueryKeys={[
          ['tickets', 'project', props.projectKey],
          ['project', props.projectKey],
        ]}
      />
      {/* Main content and right gutter */}
      <DetailPageBody
        side={
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ProjectKeyDatesSection project={data.project} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ProjectAttachmentsSection
                projectKey={props.projectKey}
                projectId={data.project.id}
              />
            </Box>
          </>
        }
      >
        {/* Timeline */}
        <Paper variant="outlined" sx={{ padding: 3 }}>
          <ProjectDateRangeControl project={data.project} variant="wide" />
        </Paper>

        {/* Detail cards grid */}
        <Box
          gap={2}
          display="grid"
          alignItems="stretch"
          gridTemplateColumns={{
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          }}
        >
          {/* Customer & locations card */}
          <ProjectDetailCard title="Customer & locations">
            <Stack spacing={2}>
              <ProjectDetailField
                label="Customer"
                labelAdornment={
                  <Tooltip
                    title="The customer associated with this project (optional)"
                    placement="top"
                    arrow
                  >
                    <InfoOutlined
                      color="action"
                      sx={{ marginLeft: 0.5, fontSize: '16px' }}
                    />
                  </Tooltip>
                }
              >
                <EditablePropertyButton onClick={customerDialog.open}>
                  <ProjectCustomerLabel customer={data.project.customer} />
                </EditablePropertyButton>
              </ProjectDetailField>
              <ProjectDetailField
                label="Default location"
                labelAdornment={
                  <Tooltip
                    title="The primary location where assets from this project will be assigned. This will be used as the default location for new line items, but can be overridden for individual line items."
                    placement="top"
                    arrow
                  >
                    <InfoOutlined
                      color="action"
                      sx={{ marginLeft: 0.5, fontSize: '16px' }}
                    />
                  </Tooltip>
                }
              >
                <EditablePropertyButton onClick={defaultLocationDialog.open}>
                  {data.project.defaultLocation ? (
                    <LocationName
                      location={data.project.defaultLocation}
                      variant="body2"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Not set
                    </Typography>
                  )}
                </EditablePropertyButton>
              </ProjectDetailField>
              <ProjectDetailField
                label="Locations"
                labelAdornment={
                  <Tooltip
                    title="Real-time display of all locations where line items from this project are currently configured"
                    placement="top"
                    arrow
                  >
                    <InfoOutlined
                      color="action"
                      sx={{ marginLeft: 0.5, fontSize: '16px' }}
                    />
                  </Tooltip>
                }
              >
                <ProjectLocationsLabel locations={data.project.locations} />
              </ProjectDetailField>
            </Stack>
          </ProjectDetailCard>

          {/* Financial card */}
          <ProjectDetailCard title="Financial">
            <Stack spacing={2}>
              <ProjectDetailField
                label="Currency"
                labelAdornment={
                  <Tooltip
                    title={
                      data.project.currency
                        ? PROJECT_CURRENCY_SOURCE_TOOLTIP[
                            data.project.currency.source
                          ]
                        : PROJECT_CURRENCY_UNSET_TOOLTIP
                    }
                    placement="top"
                    arrow
                  >
                    <InfoOutlined
                      color="action"
                      sx={{ marginLeft: 0.5, fontSize: '16px' }}
                    />
                  </Tooltip>
                }
              >
                <Typography
                  variant="body2"
                  color={
                    data.project.currency ? 'text.primary' : 'text.secondary'
                  }
                  paddingY={0.5}
                >
                  {data.project.currency
                    ? data.project.currency.isoCode
                    : 'Not set'}
                </Typography>
              </ProjectDetailField>
              <ProjectDetailField
                label="Project value"
                labelAdornment={
                  <Tooltip
                    title="The total value of the project, which can be configured manually inclusive of one-time payments, discounts, credit, and other adjustments"
                    placement="top"
                    arrow
                  >
                    <InfoOutlined
                      color="action"
                      sx={{ marginLeft: 0.5, fontSize: '16px' }}
                    />
                  </Tooltip>
                }
              >
                <ProjectTotalContractValueControl project={data.project} />
              </ProjectDetailField>
              <ProjectDetailField
                label="ARR"
                labelAdornment={
                  <Tooltip
                    title="Annual recurring revenue (ARR) is computed automatically based on the project's recurring line items (monthly, quarterly, annually), excluding one-time items"
                    placement="top"
                    arrow
                  >
                    <InfoOutlined
                      color="action"
                      sx={{ marginLeft: 0.5, fontSize: '16px' }}
                    />
                  </Tooltip>
                }
              >
                {data.project.annualRecurringRevenue.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    paddingY={0.5}
                  >
                    Not set
                  </Typography>
                ) : (
                  data.project.annualRecurringRevenue.map((arr) => (
                    <Typography
                      key={arr.isoCode}
                      variant="body2"
                      color="text.primary"
                      paddingY={0.5}
                    >
                      {formatCurrencyValue(arr)}
                    </Typography>
                  ))
                )}
              </ProjectDetailField>
            </Stack>
          </ProjectDetailCard>

          {/* Description card */}
          <ProjectDetailCard title="Description">
            <Box
              sx={{
                minHeight: 0,
                overflow: 'hidden',
                paddingLeft: 1,
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ProjectDescriptionControl project={data.project} />
            </Box>
          </ProjectDetailCard>
        </Box>

        <ProjectWorkflowDisplay project={data.project} queryKey={queryKey} />

        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            marginBottom={2}
            sx={{ borderBottom: '1px solid #E0E0E0' }}
            marginX={1}
          >
            {/*
                Unlike the order pages, the project page has the workflow
                strip and detail cards above the tabs, so tab clicks keep
                the current scroll position instead of resetting to the top
                (the default the vertical tab rail also opted out of)
              */}
            <HorizontalNavigationLink
              to="/projects/$projectKey"
              params={{ projectKey: props.projectKey }}
              activeOptions={{ exact: true }}
              resetScroll={false}
            >
              Line items
            </HorizontalNavigationLink>
            <HorizontalNavigationLink
              to="/projects/$projectKey/billing-details"
              params={{ projectKey: props.projectKey }}
              resetScroll={false}
            >
              Billing
            </HorizontalNavigationLink>
            <HorizontalNavigationLink
              to="/projects/$projectKey/fulfillment"
              params={{ projectKey: props.projectKey }}
              resetScroll={false}
            >
              Fulfillment
            </HorizontalNavigationLink>
            <HorizontalNavigationLink
              to="/projects/$projectKey/members"
              params={{ projectKey: props.projectKey }}
              resetScroll={false}
            >
              Membership
            </HorizontalNavigationLink>
            <HorizontalNavigationLink
              to="/projects/$projectKey/notes"
              params={{ projectKey: props.projectKey }}
              resetScroll={false}
            >
              Notes ({data.project.noteCount})
            </HorizontalNavigationLink>
            <HorizontalNavigationLink
              to="/projects/$projectKey/tickets"
              params={{ projectKey: props.projectKey }}
              resetScroll={false}
            >
              Tickets ({data.project.ticketCount})
            </HorizontalNavigationLink>
            <HorizontalNavigationLink
              to="/projects/$projectKey/costs"
              params={{ projectKey: props.projectKey }}
              resetScroll={false}
            >
              Costs ({data.project.costCount + data.project.timeActivityCount})
            </HorizontalNavigationLink>
            <HorizontalNavigationLink
              to="/projects/$projectKey/audit"
              params={{ projectKey: props.projectKey }}
              resetScroll={false}
            >
              Audit log
            </HorizontalNavigationLink>
          </Stack>
          <SupportErrorBoundary message="An error occurred.">
            <ProjectProvider value={{ project: data.project, queryKey }}>
              {/*
                Wrap the tab outlet in its own Suspense boundary so that a
                child tab's `useSuspenseQuery` (tickets, costs, audit, etc.)
                only suspends the tab content area. Without
                this, suspension bubbles up to the page-level Suspense in
                `ProjectRoute`, which replaces the entire layout — header,
                workflow strip, and tab strip included — with the much
                shorter `ProjectDetailsSkeleton`. That collapse is what
                yanks the viewport back to the top of the page when a user
                clicks a tab.
              */}
              <Suspense>
                <Outlet />
              </Suspense>
            </ProjectProvider>
          </SupportErrorBoundary>
        </Box>
      </DetailPageBody>
    </Container>
  );
}
